import React, { useState, useEffect } from 'react';
import {
    Card,
    CardBody,
    Row,
    Col,
    Table,
    Badge,
    Button,
    Alert
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import {
    Wallet,
    TrendingUp,
    TrendingDown,
    Clock,
    Bell,
    ArrowUp,
    ArrowDown,
    RefreshCw,
    Eye,
    PieChart,
    BarChart3
} from 'lucide-react';
import { Line, Doughnut } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-toastify';

// Register Chart.js components
Chart.register(...registerables);

const CustomerDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [userData, setUserData] = useState(null);
    const [balances, setBalances] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [orders, setOrders] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [error, setError] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError('');

            // Get current session
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            if (sessionError || !session) {
                throw new Error('Not authenticated');
            }

            const userId = session.user.id;

            // Fetch all data in parallel
            const [
                profileData,
                balancesData,
                transactionsData,
                ordersData,
                notificationsData,
                sessionsData,
                analyticsData
            ] = await Promise.all([
                // User profile
                supabase.from('profiles').select('*').eq('user_id', userId).single(),

                // Balances with assets (only where balance > 0)
                supabase
                    .from('user_balances')
                    .select(`
                        *,
                        assets (*)
                    `)
                    .eq('user_id', userId)
                    .gt('balance', 0)
                    .order('balance', { ascending: false }),

                // Last 30 days transactions for analytics
                supabase
                    .from('transactions')
                    .select('*')
                    .eq('user_id', userId)
                    .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
                    .order('created_at', { ascending: true }),

                // Last 30 days orders for analytics
                supabase
                    .from('orders')
                    .select('*')
                    .eq('user_id', userId)
                    .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
                    .order('created_at', { ascending: true }),

                // Last 5 notifications
                supabase
                    .from('notifications')
                    .select('*')
                    .eq('user_id', userId)
                    .order('created_at', { ascending: false })
                    .limit(5),

                // Last login sessions
                supabase
                    .from('sessions')
                    .select('*')
                    .eq('user_id', userId)
                    .order('created_at', { ascending: false })
                    .limit(3),

                // Additional analytics data
                fetchAnalyticsData(userId)
            ]);

            // Handle errors
            const errors = [
                profileData.error,
                balancesData.error,
                transactionsData.error,
                ordersData.error,
                notificationsData.error,
                sessionsData.error
            ].filter(error => error && error.code !== 'PGRST116'); // Ignore "not found" errors

            if (errors.length > 0) {
                console.warn('Some data fetch errors:', errors);
            }

            setUserData({
                id: userId,
                email: session.user.email,
                profile: profileData.data
            });

            setBalances(balancesData.data || []);
            setTransactions(transactionsData.data || []);
            setOrders(ordersData.data || []);
            setNotifications(notificationsData.data || []);
            setSessions(sessionsData.data || []);
            setAnalyticsData(analyticsData);

        } catch (error) {
            console.error('Failed to load dashboard data:', error);
            setError(error.message);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchAnalyticsData = async (userId) => {
        // Fetch additional data for analytics
        const [
            transactionStats,
            orderStats,
            balanceHistory
        ] = await Promise.all([
            // Transaction statistics by type and status
            supabase
                .from('transactions')
                .select('transaction_type, status, amount')
                .eq('user_id', userId)
                .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),

            // Order statistics by side and status
            supabase
                .from('orders')
                .select('side, status, type, amount, filled_amount')
                .eq('user_id', userId)
                .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),

            // Balance history for chart
            supabase
                .from('balance_history')
                .select('*')
                .eq('user_id', userId)
                .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
                .order('created_at', { ascending: true })
        ]);

        return {
            transactionStats: transactionStats.data || [],
            orderStats: orderStats.data || [],
            balanceHistory: balanceHistory.data || []
        };
    };

    const refreshData = async () => {
        setRefreshing(true);
        await fetchDashboardData();
    };

    // Chart data configurations
    const getTransactionTrendsData = () => {
        const last7Days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - (6 - i));
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        const dailyData = last7Days.map(day => {
            const dayTransactions = analyticsData?.transactionStats.filter(t => {
                const transactionDate = new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return transactionDate === day;
            }) || [];

            return dayTransactions.reduce((sum, t) => sum + parseFloat(t.amount), 0);
        });

        return {
            labels: last7Days,
            datasets: [
                {
                    label: 'Transaction Volume',
                    data: dailyData,
                    borderColor: 'rgb(59, 130, 246)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4,
                    fill: true
                }
            ]
        };
    };

    const getTransactionTypeData = () => {
        const types = ['deposit', 'withdrawal', 'trade'];
        const typeCounts = types.map(type =>
            analyticsData?.transactionStats.filter(t => t.transaction_type === type).length || 0
        );

        return {
            labels: types.map(type => type.charAt(0).toUpperCase() + type.slice(1)),
            datasets: [
                {
                    data: typeCounts,
                    backgroundColor: [
                        'rgba(34, 197, 94, 0.8)',
                        'rgba(239, 68, 68, 0.8)',
                        'rgba(59, 130, 246, 0.8)'
                    ],
                    borderColor: [
                        'rgb(34, 197, 94)',
                        'rgb(239, 68, 68)',
                        'rgb(59, 130, 246)'
                    ],
                    borderWidth: 2
                }
            ]
        };
    };

    const getOrderStatusData = () => {
        const statuses = ['completed', 'pending', 'failed', 'cancelled'];
        const statusCounts = statuses.map(status =>
            analyticsData?.orderStats.filter(o => o.status === status).length || 0
        );

        return {
            labels: statuses.map(status => status.charAt(0).toUpperCase() + status.slice(1)),
            datasets: [
                {
                    data: statusCounts,
                    backgroundColor: [
                        'rgba(34, 197, 94, 0.8)',
                        'rgba(253, 186, 116, 0.8)',
                        'rgba(239, 68, 68, 0.8)',
                        'rgba(156, 163, 175, 0.8)'
                    ],
                    borderColor: [
                        'rgb(34, 197, 94)',
                        'rgb(253, 186, 116)',
                        'rgb(239, 68, 68)',
                        'rgb(156, 163, 175)'
                    ],
                    borderWidth: 2
                }
            ]
        };
    };

    const getAssetDistributionData = () => {
        const cryptoBalances = balances.filter(b => b.assets?.asset_type === 'crypto');
        const fiatBalances = balances.filter(b => b.assets?.asset_type === 'fiat');

        const cryptoTotal = cryptoBalances.reduce((sum, b) => sum + parseFloat(b.balance), 0);
        const fiatTotal = fiatBalances.reduce((sum, b) => sum + parseFloat(b.balance), 0);

        return {
            labels: ['Cryptocurrency', 'Fiat'],
            datasets: [
                {
                    data: [cryptoTotal, fiatTotal],
                    backgroundColor: [
                        'rgba(139, 92, 246, 0.8)',
                        'rgba(59, 130, 246, 0.8)'
                    ],
                    borderColor: [
                        'rgb(139, 92, 246)',
                        'rgb(59, 130, 246)'
                    ],
                    borderWidth: 2
                }
            ]
        };
    };

    const getOrderSideData = () => {
        const buyOrders = analyticsData?.orderStats.filter(o => o.side === 'buy').length || 0;
        const sellOrders = analyticsData?.orderStats.filter(o => o.side === 'sell').length || 0;

        return {
            labels: ['Buy Orders', 'Sell Orders'],
            datasets: [
                {
                    data: [buyOrders, sellOrders],
                    backgroundColor: [
                        'rgba(34, 197, 94, 0.8)',
                        'rgba(239, 68, 68, 0.8)'
                    ],
                    borderColor: [
                        'rgb(34, 197, 94)',
                        'rgb(239, 68, 68)'
                    ],
                    borderWidth: 2
                }
            ]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            }
        }
    };

    const lineChartOptions = {
        ...chartOptions,
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    const getTransactionIcon = (type) => {
        switch (type) {
            case 'deposit': return <ArrowDown size={16} className="text-success" />;
            case 'withdrawal': return <ArrowUp size={16} className="text-danger" />;
            case 'trade': return <TrendingUp size={16} className="text-info" />;
            default: return <Clock size={16} className="text-warning" />;
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'completed': { color: 'success', text: 'Completed' },
            'pending': { color: 'warning', text: 'Pending' },
            'failed': { color: 'danger', text: 'Failed' },
            'cancelled': { color: 'secondary', text: 'Cancelled' },
            'active': { color: 'info', text: 'Active' },
            'filled': { color: 'success', text: 'Filled' },
            'partial': { color: 'warning', text: 'Partial' }
        };

        const config = statusConfig[status] || { color: 'secondary', text: status };
        return <Badge color={config.color}>{config.text}</Badge>;
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                <div className="text-center">
                    <RefreshCw size={32} className="spinning text-primary mb-2" />
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="customer-dashboard container-fluid py-4">
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1 className="h3 mb-1 text-gray-800">Customer Dashboard</h1>
                            <p className="text-muted mb-0">
                                Welcome back, {userData?.email}
                                {sessions[0] && (
                                    <span className="ms-2">
                                        • Last login: {formatDate(sessions[0].created_at)}
                                    </span>
                                )}
                            </p>
                        </div>
                        <Button
                            color="outline-primary"
                            size="sm"
                            onClick={refreshData}
                            disabled={refreshing}
                        >
                            <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
                            {refreshing ? ' Refreshing...' : ' Refresh'}
                        </Button>
                    </div>
                </Col>
            </Row>

            {error && (
                <Alert color="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            {/* Analytics Overview Section */}
            <Row className="mb-4">
                <Col lg={8}>
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">
                                    <BarChart3 size={20} className="me-2" />
                                    Transaction Trends (Last 7 Days)
                                </h5>
                            </div>
                            <div style={{ height: '300px' }}>
                                <Line
                                    data={getTransactionTrendsData()}
                                    options={lineChartOptions}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <h5 className="card-title mb-3">
                                <PieChart size={18} className="me-2" />
                                Asset Distribution
                            </h5>
                            <div style={{ height: '250px' }}>
                                <Doughnut
                                    data={getAssetDistributionData()}
                                    options={chartOptions}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Charts Row 2 */}
            <Row className="mb-4">
                <Col md={6} lg={3} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <h6 className="card-title text-center mb-3">Transaction Types</h6>
                            <div style={{ height: '200px' }}>
                                <Doughnut
                                    data={getTransactionTypeData()}
                                    options={chartOptions}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={6} lg={3} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <h6 className="card-title text-center mb-3">Order Status</h6>
                            <div style={{ height: '200px' }}>
                                <Doughnut
                                    data={getOrderStatusData()}
                                    options={chartOptions}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={6} lg={3} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <h6 className="card-title text-center mb-3">Buy/Sell Ratio</h6>
                            <div style={{ height: '200px' }}>
                                <Doughnut
                                    data={getOrderSideData()}
                                    options={chartOptions}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={6} lg={3} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <h6 className="card-title text-center mb-3">Quick Stats</h6>
                            <div className="text-center">
                                <div className="mb-3">
                                    <h4 className="text-primary mb-1">
                                        {analyticsData?.transactionStats.length || 0}
                                    </h4>
                                    <small className="text-muted">Total Transactions</small>
                                </div>
                                <div className="mb-3">
                                    <h4 className="text-success mb-1">
                                        {analyticsData?.orderStats.length || 0}
                                    </h4>
                                    <small className="text-muted">Total Orders</small>
                                </div>
                                <div>
                                    <h4 className="text-info mb-1">
                                        {balances.length}
                                    </h4>
                                    <small className="text-muted">Active Assets</small>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Balances Section */}
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">
                                    <Wallet size={20} className="me-2" />
                                    Asset Balances
                                </h5>
                                <Badge color="primary">
                                    {balances.length} assets
                                </Badge>
                            </div>

                            {balances.length === 0 ? (
                                <Alert color="info" className="text-center mb-0">
                                    <Wallet size={32} className="mb-2 text-muted" />
                                    <h6>No assets with balance yet</h6>
                                    <p className="mb-2">Start by depositing funds to your wallet</p>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => navigate('/customer/dashboard/deposit')}
                                    >
                                        Make First Deposit
                                    </Button>
                                </Alert>
                            ) : (
                                <Row>
                                    {balances.map((balance) => (
                                        <Col key={balance.asset_id} md={6} lg={4} className="mb-3">
                                            <Card className="h-100 border-0 bg-light">
                                                <CardBody className="p-3">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <h6 className="mb-1 fw-bold">
                                                                {balance.assets?.currency_code}
                                                            </h6>
                                                            <p className="text-muted mb-1 small">
                                                                {balance.assets?.currency_name}
                                                            </p>
                                                            <h5 className="text-primary mb-0">
                                                                {parseFloat(balance.balance).toLocaleString()} {balance.assets?.currency_code}
                                                            </h5>
                                                        </div>
                                                        <Badge color={balance.assets?.asset_type === 'fiat' ? 'success' : 'warning'}>
                                                            {balance.assets?.asset_type}
                                                        </Badge>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Quick Stats & Recent Activity */}
            <Row className="mb-4">
                {/* Last 5 Transactions */}
                <Col lg={6} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">Recent Transactions</h5>
                                <Link to="/customer/dashboard/history" className="btn btn-sm btn-outline-primary">
                                    <Eye size={14} className="me-1" />
                                    View All
                                </Link>
                            </div>

                            {transactions.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    <Clock size={24} className="mb-2" />
                                    <p className="mb-0">No transactions yet</p>
                                </div>
                            ) : (
                                <div className="list-group list-group-flush">
                                    {transactions.slice(0, 5).map((transaction) => (
                                        <div key={transaction.id} className="list-group-item px-0">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                    {getTransactionIcon(transaction.transaction_type)}
                                                    <div className="ms-2">
                                                        <h6 className="mb-0 text-capitalize">
                                                            {transaction.transaction_type}
                                                        </h6>
                                                        <small className="text-muted">
                                                            {formatDate(transaction.created_at)}
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    <div className={`fw-bold ${transaction.transaction_type === 'deposit' ? 'text-success' : 'text-danger'
                                                        }`}>
                                                        {transaction.transaction_type === 'deposit' ? '+' : '-'}
                                                        {formatCurrency(transaction.amount)}
                                                    </div>
                                                    {getStatusBadge(transaction.status)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                {/* Last 5 Orders */}
                <Col lg={6} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">Recent Orders</h5>
                                <Link to="/customer/orders/history" className="btn btn-sm btn-outline-primary">
                                    <Eye size={14} className="me-1" />
                                    View All
                                </Link>
                            </div>

                            {orders.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    <TrendingUp size={24} className="mb-2" />
                                    <p className="mb-0">No orders placed yet</p>
                                </div>
                            ) : (
                                <div className="list-group list-group-flush">
                                    {orders.slice(0, 5).map((order) => (
                                        <div key={order.id} className="list-group-item px-0">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="mb-0 text-capitalize">
                                                        {order.side} {order.type}
                                                    </h6>
                                                    <small className="text-muted">
                                                        {order.pair} • {formatDate(order.created_at)}
                                                    </small>
                                                </div>
                                                <div className="text-end">
                                                    <div className="fw-bold">
                                                        {parseFloat(order.amount).toLocaleString()}
                                                    </div>
                                                    {getStatusBadge(order.status)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Quick Actions - Deposit & Withdraw */}
            <Row>
                <Col md={6} className="mb-3">
                    <Card className="bg-primary text-white shadow-sm">
                        <CardBody className="text-center">
                            <ArrowDown size={32} className="mb-2" />
                            <h5>Deposit Funds</h5>
                            <p className="mb-3">Add funds to your wallet to start trading</p>
                            <Button
                                color="light"
                                onClick={() => navigate('/customer/dashboard/deposit')}
                            >
                                Make Deposit
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={6} className="mb-3">
                    <Card className="bg-success text-white shadow-sm">
                        <CardBody className="text-center">
                            <ArrowUp size={32} className="mb-2" />
                            <h5>Withdraw Funds</h5>
                            <p className="mb-3">Withdraw your funds to external wallets</p>
                            <Button
                                color="light"
                                onClick={() => navigate('/customer/dashboard/withdraw')}
                            >
                                Make Withdrawal
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <style jsx>{`
                .spinning {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default CustomerDashboard;